import {
  createTemplateTheme,
  type CodeComponentTemplateBranding,
} from './branding';

export function createMultistepFormSource(
  branding: CodeComponentTemplateBranding = {}
): string {
  const {themeSource, primaryColor} = createTemplateTheme(branding, '#2563eb');

  return `import { declareComponent } from "@webflow/react";
import { props } from "@webflow/data-types";
import { useEffect, useId, useRef, useState } from "react";

${themeSource}

/* ── form data ── */
const STEPS = [
  { id: 1, label: "Contact", fields: ["fullName", "email"] },
  { id: 2, label: "Company", fields: ["companyName", "companySize", "role"] },
  { id: 3, label: "Goals", fields: ["goal"] },
];

const COMPANY_SIZES = [
  { value: "1-10", label: "1–10 employees" },
  { value: "11-50", label: "11–50 employees" },
  { value: "51-200", label: "51–200 employees" },
  { value: "201-1000", label: "201–1,000 employees" },
  { value: "1000+", label: "1,000+ employees" },
];

const ROLES = [
  { value: "founder", label: "Founder / Executive" },
  { value: "marketing", label: "Marketing" },
  { value: "product", label: "Product" },
  { value: "engineering", label: "Engineering" },
  { value: "other", label: "Other" },
];

const GOALS = [
  {
    value: "launch",
    title: "Launch a new site",
    desc: "Starting from scratch with a new project.",
  },
  {
    value: "migrate",
    title: "Migrate an existing site",
    desc: "Moving off another platform or CMS.",
  },
  {
    value: "scale",
    title: "Scale content operations",
    desc: "Publishing more, faster, across a team.",
  },
  {
    value: "other",
    title: "Something else",
    desc: "We'll follow up to learn more.",
  },
];

const GOAL_LABELS = {
  launch: "launch a new site",
  migrate: "migrate an existing site",
  scale: "scale content operations",
  other: "something else",
};

const INITIAL = {
  fullName: "",
  email: "",
  companyName: "",
  companySize: "",
  role: "",
  goal: "",
};

function validate(step, form) {
  const errors: Record<string, string> = {};
  if (step === 1) {
    if (!form.fullName.trim()) errors.fullName = "Enter your full name.";
    if (!/^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$/.test(form.email.trim())) {
      errors.email = "Enter a valid work email address.";
    }
  }
  if (step === 2) {
    if (!form.companyName.trim()) errors.companyName = "Enter your company name.";
    if (!form.companySize) errors.companySize = "Select your company size.";
    if (!form.role) errors.role = "Select your role.";
  }
  if (step === 3) {
    if (!form.goal) errors.goal = "Choose one option to continue.";
  }
  return errors;
}

function CheckIcon({ className = "h-3 w-3" }) {
  return (
    <svg className={className} viewBox="0 0 14 11" fill="none" aria-hidden="true">
      <path
        d="M1 5.5L5 9.5L13 1"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// Replaces the native select arrow, which differs across browsers.
function SelectChevron() {
  return (
    <svg
      className="pointer-events-none absolute inset-y-0 right-3 my-auto h-2 w-3 text-[color:var(--template-muted)]"
      viewBox="0 0 12 8"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M1 1.5L6 6.5L11 1.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function Field({ id, label = "", error, children }) {
  return (
    <div className="mb-5">
      {label ? (
        <label htmlFor={id} className="mb-1.5 block text-sm font-medium text-[color:var(--template-ink)]">
          {label}
        </label>
      ) : null}
      {children}
      <p id={id + "-error"} className="mt-1.5 text-sm text-red-600 empty:hidden" aria-live="polite">
        {error}
      </p>
    </div>
  );
}

// Shadow DOM ignores Tailwind's @property defaults: set border and outline
// styles explicitly and avoid shadow or ring utilities.
const inputClass = (error) =>
  error
    ? "w-full rounded-lg border border-solid border-red-500 bg-[var(--template-surface)] px-3 py-2.5 text-base text-[color:var(--template-ink)] placeholder:text-[color:var(--template-muted)] focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-red-500"
    : "w-full rounded-lg border border-solid border-[color:var(--template-border-strong)] bg-[var(--template-surface)] px-3 py-2.5 text-base text-[color:var(--template-ink)] placeholder:text-[color:var(--template-muted)] focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-[var(--template-accent)]";

const fieldProps = (id, key, errors) => ({
  id: id + "-" + key,
  name: key,
  "aria-invalid": Boolean(errors[key]),
  "aria-describedby": id + "-" + key + "-error",
});

const primaryButton =
  "rounded-[var(--template-button-radius,0.5rem)] bg-[var(--template-accent)] px-5 py-2.5 text-sm font-medium text-[color:var(--template-accent-text,white)] hover:bg-[var(--template-accent-hover)] " +
  BUTTON_TEXT + " " +
  FOCUS_RING;
const backButton =
  "rounded-[var(--template-button-radius,0.5rem)] px-1.5 py-2.5 text-sm font-medium text-[color:var(--template-muted)] hover:text-[color:var(--template-ink)] " +
  BUTTON_TEXT + " " +
  FOCUS_RING;

/* ── main component ── */
export function MultiStepForm({ primaryColor = ${primaryColor} }) {
  const id = useId();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(INITIAL);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const formRef = useRef(null);
  const didNavigate = useRef(false);

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const focusField = (key) => {
    formRef.current?.querySelector('[name="' + key + '"]')?.focus();
  };

  // Move focus into the new step only after the visitor navigates, so the
  // form never steals focus when the page loads.
  useEffect(() => {
    if (!didNavigate.current) return;
    const firstField = STEPS[step - 1]?.fields[0];
    if (firstField) focusField(firstField);
    else formRef.current?.querySelector("h2")?.focus();
  }, [step]);

  const tryAdvance = (nextStep) => {
    const nextErrors = validate(step, form);
    setErrors(nextErrors);
    const firstError = STEPS[step - 1].fields.find((key) => nextErrors[key]);
    if (firstError) {
      focusField(firstError);
      return;
    }
    didNavigate.current = true;
    setStep(nextStep);
  };

  const goBack = () => {
    setErrors({});
    didNavigate.current = true;
    setStep((s) => Math.max(1, s - 1));
  };

  // Enter submits the form from any step, so only advance one step.
  const handleSubmit = (e) => {
    e.preventDefault();
    tryAdvance(step + 1);
  };

  return (
    <div
      className="mx-auto w-full max-w-lg"
      style={themeStyle(primaryColor)}
    >
      <p className="sr-only" aria-live="polite">
        {step <= 3 ? "Step " + step + " of 3" : "Complete"}
      </p>

      {/* 🎨 BRAND COLOR: Stepper */}
      <nav aria-label="Signup progress" className="mb-8">
        <ol className="m-0 flex list-none items-start p-0">
          {STEPS.map((s, idx) => {
            const done = step > s.id;
            const active = step === s.id;
            return (
              <li key={s.id} className="flex flex-1 items-start last:flex-none">
                <div className="flex w-[72px] flex-col items-center text-center">
                  <div
                    className={
                      done
                        ? "flex h-7 w-7 items-center justify-center rounded-full border border-solid border-[color:var(--template-accent)] bg-[var(--template-accent)] text-xs font-medium text-[color:var(--template-accent-text,white)] transition-colors"
                        : active
                          ? "flex h-7 w-7 items-center justify-center rounded-full border border-solid border-transparent bg-[var(--template-secondary)] text-xs font-medium text-[color:var(--template-accent)] transition-colors"
                          : "flex h-7 w-7 items-center justify-center rounded-full border border-solid border-[color:var(--template-border-strong)] bg-[var(--template-surface)] text-xs font-medium text-[color:var(--template-muted)] transition-colors"
                    }
                    aria-current={active ? "step" : undefined}
                  >
                    {done ? <CheckIcon className="h-3 w-3" /> : s.id}
                  </div>
                  <div className={done || active ? "mt-2 text-sm text-[color:var(--template-ink)]" : "mt-2 " + TYPE.small}>
                    {s.label}
                  </div>
                </div>
                {idx < STEPS.length - 1 && (
                  <div className="mt-3.5 h-0.5 flex-1 overflow-hidden rounded-full bg-[var(--template-border)]">
                    <div
                      className="h-full origin-left bg-[var(--template-accent-secondary)] transition-transform duration-500 motion-reduce:transition-none"
                      style={{ transform: done ? "scaleX(1)" : "scaleX(0)" }}
                    />
                  </div>
                )}
              </li>
            );
          })}
        </ol>
      </nav>

      <div
        className="border border-solid border-[color:var(--template-border)] bg-[var(--template-surface)] p-8 max-[479px]:p-6"
        style={{ boxShadow: "0 1px 2px 0 rgb(0 0 0 / 0.05)" }}
      >
        <form ref={formRef} onSubmit={handleSubmit} noValidate>
          {/* Step 1 */}
          {step === 1 && (
            <div>
              {/* HEADING - Size: 2xl, Weight: 600 */}
              <h2 className={"m-0 text-balance " + TYPE.h2}>
                Let&apos;s start with the basics
              </h2>
              <p className={"mb-6 mt-1 " + TYPE.small}>
                We&apos;ll use this to set up your workspace and send your login link.
              </p>

              <Field id={id + "-fullName"} label="Full name" error={errors.fullName}>
                <input
                  {...fieldProps(id, "fullName", errors)}
                  type="text"
                  autoComplete="name"
                  placeholder="Jordan Lee"
                  value={form.fullName}
                  onChange={(e) => setField("fullName", e.target.value)}
                  className={inputClass(errors.fullName)}
                />
              </Field>

              <Field id={id + "-email"} label="Work email" error={errors.email}>
                <input
                  {...fieldProps(id, "email", errors)}
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  spellCheck={false}
                  placeholder="jordan@company.com"
                  value={form.email}
                  onChange={(e) => setField("email", e.target.value)}
                  className={inputClass(errors.email)}
                />
              </Field>

              <div className="mt-8 flex items-center justify-end">
                <button type="button" onClick={() => tryAdvance(2)} className={primaryButton}>
                  Continue
                </button>
              </div>
            </div>
          )}

          {/* Step 2 */}
          {step === 2 && (
            <div>
              <h2 className={"m-0 text-balance " + TYPE.h2}>
                Tell us about your company
              </h2>
              <p className={"mb-6 mt-1 " + TYPE.small}>
                This helps us tailor onboarding and pricing to your team&apos;s size.
              </p>

              <Field id={id + "-companyName"} label="Company name" error={errors.companyName}>
                <input
                  {...fieldProps(id, "companyName", errors)}
                  type="text"
                  autoComplete="organization"
                  placeholder="Acme Inc."
                  value={form.companyName}
                  onChange={(e) => setField("companyName", e.target.value)}
                  className={inputClass(errors.companyName)}
                />
              </Field>

              <Field id={id + "-companySize"} label="Company size" error={errors.companySize}>
                <div className="relative">
                  <select
                    {...fieldProps(id, "companySize", errors)}
                    value={form.companySize}
                    onChange={(e) => setField("companySize", e.target.value)}
                    className={inputClass(errors.companySize) + " appearance-none pr-9"}
                  >
                    <option value="">Select an option</option>
                    {COMPANY_SIZES.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                  <SelectChevron />
                </div>
              </Field>

              <Field id={id + "-role"} label="Your role" error={errors.role}>
                <div className="relative">
                  <select
                    {...fieldProps(id, "role", errors)}
                    value={form.role}
                    onChange={(e) => setField("role", e.target.value)}
                    className={inputClass(errors.role) + " appearance-none pr-9"}
                  >
                    <option value="">Select an option</option>
                    {ROLES.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                  <SelectChevron />
                </div>
              </Field>

              <div className="mt-8 flex items-center justify-between">
                <button type="button" onClick={goBack} className={backButton}>
                  Back
                </button>
                <button type="button" onClick={() => tryAdvance(3)} className={primaryButton}>
                  Continue
                </button>
              </div>
            </div>
          )}

          {/* Step 3 */}
          {step === 3 && (
            <div>
              <h2 className={"m-0 text-balance " + TYPE.h2}>
                What are you hoping to do?
              </h2>
              <p className={"mb-6 mt-1 " + TYPE.small}>
                Pick the option closest to your goal — you can always change this later.
              </p>

              <Field id={id + "-goal"} error={errors.goal}>
                <div className="grid gap-2.5" role="radiogroup" aria-label="Primary goal" aria-describedby={id + "-goal-error"}>
                  {GOALS.map((g) => {
                    const checked = form.goal === g.value;
                    return (
                      <label
                        key={g.value}
                        className={
                          checked
                            ? "flex cursor-pointer items-start gap-3 rounded-xl border border-solid border-[color:var(--template-accent)] bg-[var(--template-secondary)] p-3.5 transition-colors"
                            : "flex cursor-pointer items-start gap-3 rounded-xl border border-solid border-[color:var(--template-border)] p-3.5 transition-colors hover:bg-[var(--template-subtle)]"
                        }
                      >
                        <input
                          type="radio"
                          name="goal"
                          value={g.value}
                          checked={checked}
                          onChange={() => setField("goal", g.value)}
                          className="mt-1 h-4 w-4 accent-[var(--template-accent)]"
                        />
                        <span>
                          <span className="block text-sm font-medium text-[color:var(--template-ink)]">{g.title}</span>
                          <span className={"mt-0.5 block " + TYPE.small}>{g.desc}</span>
                        </span>
                      </label>
                    );
                  })}
                </div>
              </Field>

              <div className="mt-8 flex items-center justify-between">
                <button type="button" onClick={goBack} className={backButton}>
                  Back
                </button>
                <button type="submit" className={primaryButton}>
                  Create my workspace
                </button>
              </div>
            </div>
          )}

          {/* Success */}
          {step === 4 && (
            <div>
              <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-full bg-[var(--template-secondary)] text-[color:var(--template-accent)]">
                <CheckIcon className="h-5 w-5" />
              </div>
              <h2 tabIndex={-1} className={"m-0 text-balance outline-none " + TYPE.h2}>
                You&apos;re all set
              </h2>
              <p className={"mt-1 " + TYPE.small}>
                We&apos;ve created your workspace and sent a login link to your inbox.
              </p>
              <p className={"mt-5 border-t border-solid border-[color:var(--template-border)] pt-4 " + TYPE.small}>
                Confirmation sent to{" "}
                <strong className="break-words text-[color:var(--template-ink)]">{form.email.trim()}</strong>. Goal on
                file:{" "}
                <strong className="text-[color:var(--template-ink)]">{GOAL_LABELS[form.goal] || "—"}</strong>.
              </p>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}

export default declareComponent(MultiStepForm, {
  name: "Multistep Form",
  description: "Three-step signup form with a progress stepper, inline validation, and a success state.",
  group: "Sections",
  props: {
    primaryColor: props.Text({
      name: "Primary Brand Color",
      defaultValue: ${primaryColor},
    }),
  },
});
`;
}

export const MULTISTEP_FORM_SOURCE = createMultistepFormSource();
